import React, { useCallback, useEffect, useMemo } from 'react';

import { MenuItem } from '@mui/material';
import { IconMenuItem } from 'mui-nested-menu';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { QueryParams as createNewViewMutationParams } from 'api/mutations/createPoapView.mutation';
import { GET_POAP_PRIMARY_VIEWS_QUERY_KEY } from 'api/queries/getPoapPrimaryViews.query';
import { GET_POAP_SECONDARY_VIEWS_QUERY_KEY } from 'api/queries/getPoapSecondaryViews.query';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { UseViewsManagerProvider } from 'components/pages/ScheduleViewsPage/ViewsManager/ViewsManager.provider';
import { getActiveTabFromUrlAndTabs } from 'components/pages/ScheduleViewsPage/ViewsManager/ViewsManager.utils';
import { LabelWithMenu } from 'components/pages/ScheduleViewsPage/ViewsManager/components/LabelWithMenu/LabelWithMenu';
import { useScheduleViewIdUrlState } from 'components/pages/ScheduleViewsPage/hooks/useScheduleViewIdUrlState';

const MENU_FONT_SIZE = '13px';
const MENU_ICON_SIZE = '17px';

const PrivateViewIcon = <MuiIcon icon={'public_off'} type={'outlined'} fontSize={MENU_ICON_SIZE} />;
const PublicViewIcon = <MuiIcon icon={'public'} fontSize={MENU_ICON_SIZE} />;

export const useViewsManagerLogic = ({ api }: UseViewsManagerProvider) => {
    const [viewId, setViewId] = useScheduleViewIdUrlState();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();

    const visibleItems = api.getPrimaryViews.instance.data;
    const hiddenItems = api.getSecondaryViews.instance.data;

    const { mutateAsync: deleteViewMutation, isLoading: isLoadingDeleteView } = api.deleteView.instance;
    const { mutateAsync: duplicateViewMutation, isLoading: isLoadingDuplicateView } = api.duplicateView.instance;
    const { mutateAsync: createNewViewMutation } = api.createNewView.instance;
    const { mutateAsync: setPrivacyModeMutation, isLoading: isLoadingSetPrivacyMode } = api.setPrivacyMode.instance;
    const { mutate: setVisibilityModeMutation, isLoading: isLoadingSetVisibility } = api.setVisibilityMode.instance;
    const { mutateAsync: renameViewNameMutation } = api.renameViewName.instance;

    const onRenameView = useCallback(
        async (viewIdToRename: string, newName: string) => {
            try {
                await renameViewNameMutation(
                    { viewId: Number(viewIdToRename), newName },
                    {
                        onSuccess: () => {
                            handleSnackBar('View renamed successfully', 'success');
                        },
                        onError: (error) => {
                            DEFAULT_ERROR({ error, handleError: handleSnackBar });
                        },
                    },
                );
                await Promise.all([
                    queryClient.invalidateQueries([GET_POAP_PRIMARY_VIEWS_QUERY_KEY]),
                    queryClient.invalidateQueries([GET_POAP_SECONDARY_VIEWS_QUERY_KEY]),
                ]);
            } catch {
                // Error handled in onError
            }
        },
        [renameViewNameMutation, queryClient, handleSnackBar],
    );

    const onDuplicateView = useCallback(
        async (sourceViewId: string, currentViewName: string) => {
            const viewName = `${currentViewName}-1`;
            try {
                const response = await duplicateViewMutation({
                    viewId: Number(sourceViewId),
                    viewName,
                });
                handleSnackBar('View duplicated successfully', 'success');
                await Promise.all([
                    queryClient.invalidateQueries([GET_POAP_PRIMARY_VIEWS_QUERY_KEY]),
                    queryClient.invalidateQueries([GET_POAP_SECONDARY_VIEWS_QUERY_KEY]),
                ]);
                if (response?.viewId != null) {
                    setViewId(String(response.viewId));
                }
            } catch (error) {
                DEFAULT_ERROR({ error: error as QueryError, handleError: handleSnackBar });
            }
        },
        [duplicateViewMutation, queryClient, handleSnackBar, setViewId],
    );

    const onCreateNewView = async (viewName: string) => {
        const payload: createNewViewMutationParams = {
            viewName,
        };

        try {
            const response = await createNewViewMutation(payload);
            handleSnackBar('View created successfully', 'success');
            await queryClient.invalidateQueries([GET_POAP_PRIMARY_VIEWS_QUERY_KEY]);
            await Promise.all([
                queryClient.invalidateQueries([GET_POAP_PRIMARY_VIEWS_QUERY_KEY]),
                queryClient.invalidateQueries([GET_POAP_SECONDARY_VIEWS_QUERY_KEY]),
            ]);
            setViewId(String(response.viewId));
        } catch (error) {
            DEFAULT_ERROR({ error: error as QueryError, handleError: handleSnackBar });
        }
    };

    const onDeleteView = useCallback(
        async (viewIdToDelete: string) => {
            const payload = {
                viewId: Number(viewIdToDelete),
            };

            await deleteViewMutation(payload, {
                onSuccess: () => {
                    handleSnackBar('View successfully deleted', 'success');
                    if (viewId === viewIdToDelete) {
                        const remainingViews = Array.isArray(visibleItems)
                            ? visibleItems.filter((v) => String(v.viewId) !== viewIdToDelete)
                            : [];
                        const nextView = remainingViews[0];
                        if (nextView) {
                            setViewId(String(nextView.viewId), { replace: true });
                        }
                    }
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            });

            await Promise.all([
                queryClient.invalidateQueries([GET_POAP_PRIMARY_VIEWS_QUERY_KEY]),
                queryClient.invalidateQueries([GET_POAP_SECONDARY_VIEWS_QUERY_KEY]),
            ]);
        },
        [deleteViewMutation, queryClient, viewId, setViewId, visibleItems],
    );

    const onSetVisibility = useCallback(
        (viewIdToSet: string, hide: boolean) => {
            setVisibilityModeMutation(
                { viewId: Number(viewIdToSet), hide },
                {
                    onSuccess: () => {
                        if (hide && viewId === viewIdToSet) {
                            const remainingViews = Array.isArray(visibleItems)
                                ? visibleItems.filter((v) => String(v.viewId) !== viewIdToSet)
                                : [];
                            const nextView = remainingViews[0];
                            if (nextView) {
                                setViewId(String(nextView.viewId), { replace: true });
                            }
                        }
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                    onSettled: () => {
                        queryClient.invalidateQueries({
                            predicate: (query) =>
                                query.queryKey[0] === GET_POAP_PRIMARY_VIEWS_QUERY_KEY ||
                                query.queryKey[0] === GET_POAP_SECONDARY_VIEWS_QUERY_KEY,
                        });
                    },
                },
            );
        },
        [queryClient, setVisibilityModeMutation, viewId, setViewId, visibleItems],
    );

    const onSetPrivacyMode = useCallback(
        async (viewIdToSet: string, isPublic: boolean) => {
            const payload = {
                viewId: Number(viewIdToSet),
                isPublic,
            };

            await setPrivacyModeMutation(payload, {
                onSuccess: () => {
                    handleSnackBar(`View successfully ${isPublic ? 'published' : 'unpublished'}`, 'success');
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            });

            await Promise.all([
                queryClient.invalidateQueries([GET_POAP_PRIMARY_VIEWS_QUERY_KEY]),
                queryClient.invalidateQueries([GET_POAP_SECONDARY_VIEWS_QUERY_KEY]),
            ]);
        },
        [queryClient, setPrivacyModeMutation],
    );

    const tabs = useMemo(() => {
        if (!Array.isArray(visibleItems)) return [];
        return visibleItems.map((view) => {
            const value = String(view.viewId);
            return {
                value: value,
                icon: view.public ? PublicViewIcon : PrivateViewIcon,
                label: (
                    <LabelWithMenu
                        onDelete={onDeleteView}
                        onRenameView={onRenameView}
                        onDuplicateView={onDuplicateView}
                        onSetVisibility={onSetVisibility}
                        onSetPrivacyMode={onSetPrivacyMode}
                        isLoadingDelete={isLoadingDeleteView}
                        isLoadingDuplicateView={isLoadingDuplicateView}
                        isLoadingSetVisibility={isLoadingSetVisibility}
                        isLoadingSetPrivacyMode={isLoadingSetPrivacyMode}
                        value={value}
                        view={view}
                    />
                ),
            };
        });
    }, [
        visibleItems,
        isLoadingDeleteView,
        isLoadingDuplicateView,
        isLoadingSetVisibility,
        isLoadingSetPrivacyMode,
        onDeleteView,
        onRenameView,
        onDuplicateView,
        onSetVisibility,
        onSetPrivacyMode,
    ]);

    const activeTab = getActiveTabFromUrlAndTabs(viewId, tabs);
    const onTabChange = (value: string) => {
        setViewId(value);
    };
    // Init the viewId url path with the first tab if it's not set and there are tabs available
    useEffect(() => {
        if (viewId === undefined && tabs.length > 0 && tabs[0].value) {
            setViewId(tabs[0].value, { replace: true });
        }
    }, [viewId, tabs, setViewId]);

    const additionalViews = useMemo(() => {
        if (!Array.isArray(hiddenItems)) return [];
        return hiddenItems.map((view) => {
            const value = String(view.viewId);
            return (
                <MenuItem>
                    <IconMenuItem
                        sx={{
                            '& .MuiTypography-root': {
                                fontSize: MENU_FONT_SIZE,
                                position: 'relative',
                            },
                            '& .MuiBox-root': {
                                alignItems: 'center',
                            },
                        }}
                        leftIcon={view.public ? PublicViewIcon : PrivateViewIcon}
                        onClick={() => {
                            onSetVisibility(value, false);
                        }}
                        label={view.viewName}
                    />
                </MenuItem>
            );
        });
    }, [hiddenItems, onSetVisibility]);
    return {
        logic: {
            tabs,
            onCreateNewView,
            activeTab,
            onTabChange,
            additionalViews,
        },
    };
};
